import React, { type ReactNode } from 'react';
import type { EditorState } from 'lexical';
import { twMerge } from 'tailwind-merge';
import { LexicalInput } from '@components/Lexical/Input';
import { useLexicalMentionsMenuContainerMarginClassName } from '@components/Lexical/useLexicalMentionsMenuBottomPadding';
import { Avatar } from '@components/basics/Images/Avatar';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';

type PostCreator = {
  id: string;
  username: string;
  publicAddress: string;
  webappUri: string;
  artist: {
    id: string;
  } | null;
  avatar: {
    id: string;
    url: string;
  } | null;
};

const AVATAR_SIZE = 40;

export function ReplyPostContent({
  isLoading,
  postCreator,
  postContent,
  onSubmit,
  isSubmitting,
  autoFocus,
}: {
  isLoading: boolean;
  postCreator: PostCreator | undefined;
  postContent: ReactNode | undefined;
  onSubmit: (lexicalContent: EditorState) => void;
  isSubmitting: boolean;
  autoFocus: boolean;
}) {
  const extraMarginClassName = useLexicalMentionsMenuContainerMarginClassName();

  return (
    <div className={twMerge('relative flex h-full flex-col gap-5', extraMarginClassName)}>
      <div className="flex h-[12] max-h-[48] w-full gap-3">
        {postCreator && !isLoading ? (
          <div className="h-10 w-10 flex-shrink-0">
            <Avatar
              size={AVATAR_SIZE}
              walletAddress={postCreator.publicAddress}
              webappUri={postCreator.webappUri}
              src={postCreator.avatar?.url}
              username={postCreator.username}
              borderRadius={AVATAR_SIZE}
            />
          </div>
        ) : (
          <div className="h-10 w-10 animate-pulse rounded-full bg-neutral200" />
        )}

        <div className="flex w-full flex-col gap-1.5">
          <div className="flex items-center gap-1">
            {postCreator && !isLoading ? (
              <>
                <span className="font-base text-base-m font-semibold text-base900">
                  {postCreator.username}
                </span>{' '}
                {!!postCreator.artist && <VerifiedTooltip size={14} portal noTooltip />}
              </>
            ) : (
              <div className="h-5 w-48 animate-pulse rounded-sm bg-neutral200" />
            )}
          </div>

          {postCreator && !isLoading ? (
            <div className="line-clamp-4">{postContent}</div>
          ) : (
            <div className="flex w-full flex-col gap-2">
              <div className="h-[14px] w-full animate-pulse rounded-sm bg-neutral200" />
              <div className="h-[14px] w-full animate-pulse rounded-sm bg-neutral200" />
            </div>
          )}
        </div>
      </div>

      {postCreator && postContent && !isLoading ? (
        <LexicalInput
          initialMentionData={undefined}
          menuAnchorClassName="z-modalDropdown"
          className="mb-1"
          loading={isSubmitting}
          onSubmit={onSubmit}
          autoFocus={autoFocus}
          characterCount={false}
          disabled={false}
          id={undefined}
          type="single-line"
          placeholder="Enter some text..."
          onChange={null}
        />
      ) : (
        <div className="h-10 w-full animate-pulse rounded-sm bg-neutral200" />
      )}
    </div>
  );
}
